// ===========================================
// Message Bubble Component
// ===========================================
// Individual message display with styling for sent/received

import { Box, Typography, Paper, Avatar } from '@mui/material';
import { format } from 'date-fns';
import { Done as DoneIcon, DoneAll as DoneAllIcon } from '@mui/icons-material';
import { Message } from '../../api/messages.api';
import { getContrastTextColor } from '../../styles/brandColors';

// ===========================================
// TYPES
// ===========================================

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export default function MessageBubble({ message, isOwn }: MessageBubbleProps) {
  const time = format(new Date(message.createdAt), 'h:mm a');
  const senderColor = message.sender.color;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        alignItems: 'flex-end',
        gap: 1,
        mb: 1.5,
      }}
    >
      {!isOwn && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: senderColor || 'primary.main',
            color: senderColor ? getContrastTextColor(senderColor) : 'primary.contrastText',
          }}
        >
          {getInitials(message.sender.firstName, message.sender.lastName)}
        </Avatar>
      )}
      <Paper
        elevation={0}
        sx={{
          maxWidth: '70%',
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: isOwn ? 'primary.main' : 'grey.100',
          color: isOwn ? 'primary.contrastText' : 'text.primary',
          borderTopLeftRadius: isOwn ? 16 : 4,
          borderTopRightRadius: isOwn ? 4 : 16,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        }}
      >
        {/* Sender name for received messages */}
        {!isOwn && (
          <Typography
            variant="caption"
            fontWeight={600}
            color="primary.main"
            display="block"
            sx={{ mb: 0.5 }}
          >
            {message.sender.firstName} {message.sender.lastName}
          </Typography>
        )}

        {/* Message content */}
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>

        {/* Time and read status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              color: isOwn ? 'primary.contrastText' : 'text.secondary',
            }}
          >
            {time}
          </Typography>
          {isOwn && (
            message.readAt ? (
              <DoneAllIcon
                sx={{
                  fontSize: 14,
                  color: 'primary.contrastText',
                  opacity: 0.7,
                }}
              />
            ) : (
              <DoneIcon
                sx={{
                  fontSize: 14,
                  color: 'primary.contrastText',
                  opacity: 0.7,
                }}
              />
            )
          )}
        </Box>
      </Paper>
    </Box>
  );
}
